import React, { useState, useEffect } from 'react';
import { RefreshCw, Unlink, RotateCw } from 'lucide-react';
import { format } from 'date-fns';
import { calendarSourcesApi, calendarConnectApi } from '../api/services';
import type { CalendarSource } from '../types/models';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { MicrosoftIcon, GoogleIcon } from './icons/BrandIcons';
import { cn } from '@/lib/utils';

function ProviderIcon({ sourceType }: { sourceType?: string }) {
  if (sourceType === 'Microsoft365') return <MicrosoftIcon />;
  if (sourceType === 'Google') return <GoogleIcon />;
  return <RefreshCw className="h-4 w-4 text-muted-foreground" />;
}

export default function ConnectCalendarCard() {
  const confirm = useConfirm();
  const [sources, setSources] = useState<CalendarSource[]>([]);
  const [connecting, setConnecting] = useState<string | null>(null);
  const [isPersonal, setIsPersonal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [disconnectingId, setDisconnectingId] = useState<string | null>(null);

  const loadSources = async () => {
    try {
      const all = await calendarSourcesApi.getAll();
      setSources(all.filter(s => s.sourceType === 'Microsoft365' || s.sourceType === 'Google'));
    } catch (err) {
      console.error('Failed to load sources', err);
    }
  };

  useEffect(() => { loadSources(); }, []);

  const handleConnect = async (provider: 'microsoft' | 'google') => {
    setConnecting(provider);
    setError(null);
    try {
      const name = provider === 'microsoft' ? 'Microsoft 365' : 'Google Calendar';
      const connectData = { name, isPersonal, eaVisibility: isPersonal ? 'TimeOnly' : undefined, returnPath: '/calendar' };
      const data = provider === 'microsoft'
        ? await calendarConnectApi.connectMicrosoft(connectData)
        : await calendarConnectApi.connectGoogle(connectData);
      if (data.authUrl) {
        window.location.href = data.authUrl;
      }
    } catch (err: any) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to start connection';
      setError(typeof msg === 'string' ? msg : 'Failed to start connection');
      setConnecting(null);
    }
  };

  // Per-row Reconnect. Derives isPersonal from the source row itself so the
  // backend dedupe key (UserId, TenantId, SourceType, IsPersonal, IsActive)
  // resolves to *this* row — preventing the page-level checkbox from
  // accidentally pointing the OAuth flow at a different row. Hit in prod
  // 2026-05-29: the page-level checkbox had drifted out of sync with the
  // intended source, so clicking "Connect Microsoft 365" matched the
  // other row, and the new mailbox's token got bound to it. Reconnecting
  // via this button bypasses that confusion entirely.
  const handleReconnect = async (source: CalendarSource) => {
    const providerKey = source.sourceType === 'Microsoft365' ? 'microsoft' : 'google';
    setConnecting(providerKey);
    setError(null);
    try {
      const connectData = {
        // Exactly this row: matching by provider and personal flag picked an
        // arbitrary one when there were duplicates.
        sourceId: source.id,
        name: source.name,
        isPersonal: !!source.isPersonal,
        eaVisibility: source.eaVisibility ?? (source.isPersonal ? 'TimeOnly' : 'Full'),
        returnPath: '/calendar',
      };
      const data = providerKey === 'microsoft'
        ? await calendarConnectApi.connectMicrosoft(connectData)
        : await calendarConnectApi.connectGoogle(connectData);
      if (data.authUrl) {
        window.location.href = data.authUrl;
      }
    } catch (err: any) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to start reconnect';
      setError(typeof msg === 'string' ? msg : 'Failed to start reconnect');
      setConnecting(null);
    }
  };

  const handleSync = async (sourceId: string) => {
    setSyncingId(sourceId);
    setError(null);
    try {
      await calendarConnectApi.sync(sourceId);
      loadSources();
    } catch (err: any) {
      const data = err.response?.data;
      // Backend wipe-alert backstop: sync would delete >25 events from this
      // source. Upsert half already committed; the delete pass is pending
      // user confirmation. Show count + a few sample titles so the user
      // knows what's about to disappear before they wave it through.
      if (err.response?.status === 409 && data?.wipeAlert) {
        const sample: string[] = Array.isArray(data.sampleTitles) ? data.sampleTitles : [];
        const sampleLine = sample.length > 0
          ? `\n\nIncluding:\n• ${sample.slice(0, 5).join('\n• ')}${data.deleteCount > sample.length ? `\n• … and ${data.deleteCount - sample.length} more` : ''}`
          : '';
        const ok = await confirm({
          title: 'Confirm calendar sync',
          body: `This sync will remove ${data.deleteCount} events from this calendar between ${data.rangeFrom} and ${data.rangeTo}. New + updated events have already been saved.${sampleLine}\n\nContinue with the removals?`,
          confirmLabel: `Remove ${data.deleteCount} events`,
          destructive: true,
        });
        if (!ok) { setSyncingId(null); return; }
        try {
          await calendarConnectApi.sync(sourceId, { force: true });
          loadSources();
        } catch (retryErr: any) {
          const retryMsg = retryErr.response?.data?.error || retryErr.response?.data?.message || 'Sync failed';
          setError(typeof retryMsg === 'string' ? retryMsg : 'Sync failed');
        }
        return;
      }
      const msg = data?.error || data?.message || 'Sync failed';
      setError(typeof msg === 'string' ? msg : 'Sync failed');
    } finally {
      setSyncingId(null);
    }
  };

  const handleDisconnect = async (sourceId: string) => {
    const ok = await confirm({
      title: 'Disconnect this calendar?',
      body: 'All synced events from this source will be removed from Cadence. You can reconnect later.',
      confirmLabel: 'Disconnect',
      destructive: true,
    });
    if (!ok) return;
    setDisconnectingId(sourceId);
    try {
      await calendarConnectApi.disconnect(sourceId);
      loadSources();
    } catch (err) {
      console.error('Failed to disconnect', err);
    } finally {
      setDisconnectingId(null);
    }
  };

  const handleTogglePersonal = async (sourceId: string, newValue: boolean) => {
    setError(null);
    try {
      await calendarSourcesApi.updateSettings(sourceId, { isPersonal: newValue });
      setSources(prev => prev.map(s => s.id === sourceId ? { ...s, isPersonal: newValue, eaVisibility: newValue ? 'TimeOnly' : 'Full' } : s));
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message || 'Failed to update setting';
      setError(typeof msg === 'string' ? msg : 'Failed to update setting');
    }
  };

  return (
    <Card className="mb-4">
      <CardContent className="pt-6">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Connect Calendar</p>

        {error && (
          <Alert variant="error" className="mb-3">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="mb-3 flex flex-col gap-2 sm:flex-row">
          <Button
            onClick={() => handleConnect('microsoft')}
            disabled={connecting !== null}
            className="gap-2 whitespace-nowrap bg-[#0078D4] px-5 font-semibold normal-case text-white hover:bg-[#106EBE]"
          >
            <MicrosoftIcon />
            {connecting === 'microsoft' ? 'Connecting...' : 'Connect Microsoft 365'}
          </Button>
          <Button
            onClick={() => handleConnect('google')}
            disabled={connecting !== null}
            className="gap-2 whitespace-nowrap border border-white/20 bg-[#1A1A1A] px-5 font-semibold normal-case text-white hover:bg-[#2D2D2D]"
          >
            <GoogleIcon />
            {connecting === 'google' ? 'Connecting...' : 'Connect Google Calendar'}
          </Button>
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <Checkbox checked={isPersonal} onCheckedChange={(v) => setIsPersonal(v === true)} />
          <span className="text-sm text-muted-foreground">
            This is a personal calendar (EA will see time blocks only, not event details)
          </span>
        </label>

        {sources.length > 0 && (
          <div className="mt-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Connected Calendars</p>
            <div className="flex flex-col gap-2">
              {sources.map((source) => {
                // Never signed in, as opposed to a sign-in the provider refused.
                const neverConnected = !!source.needsReconnect && !source.reconnectNeededSince;
                return (
                <div key={source.id} className="rounded-lg border border-border bg-muted p-3">
                  <div className="mb-1 flex items-center gap-2">
                    <ProviderIcon sourceType={source.sourceType} />
                    <span className="flex-1 text-sm font-medium">{source.name}</span>
                    <Badge
                      variant="outline"
                      className={cn(
                        'h-[22px]',
                        source.needsReconnect
                          ? 'border-amber-500 text-amber-600 dark:text-amber-400'
                          : source.isActive
                            ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
                            : '',
                      )}
                    >
                      {source.needsReconnect
                        ? (source.reconnectNeededSince ? 'Stopped syncing' : 'Disconnected')
                        : source.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    {source.providerEmail && <span>{source.providerEmail}</span>}
                    {source.needsReconnect ? (
                      <>
                        <Badge variant="outline" className="h-[18px] border-amber-500 text-[0.65rem] text-amber-600 dark:text-amber-400">
                          Needs reconnect
                        </Badge>
                        {/* A refused sign-in: say how stale the calendar is. */}
                        {source.reconnectNeededSince && source.lastSyncedAt && (
                          <span>Last synced {format(new Date(source.lastSyncedAt), 'dd MMM HH:mm')}</span>
                        )}
                      </>
                    ) : (
                      <span>
                        {source.lastSyncedAt
                          ? `Last synced ${format(new Date(source.lastSyncedAt), 'dd MMM HH:mm')}`
                          : 'Never synced'}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 flex items-center justify-between">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <label className="flex cursor-pointer items-center gap-2">
                            <Switch
                              checked={!!source.isPersonal}
                              onCheckedChange={(v) => handleTogglePersonal(source.id, v)}
                            />
                            <span className="text-xs text-muted-foreground">
                              Personal{source.isPersonal ? ' — EA sees time blocks only' : ''}
                            </span>
                          </label>
                        </TooltipTrigger>
                        <TooltipContent>
                          {source.isPersonal ? 'EA will see time blocks only' : 'EA can see full event details'}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => handleSync(source.id)}
                        // A refused sign-in can still be tried by hand (IT may
                        // have fixed the device); one that never connected can't.
                        disabled={syncingId === source.id || neverConnected}
                        title={neverConnected ? 'Reconnect calendar first' : 'Sync now'}
                      >
                        <RefreshCw className={cn('h-4 w-4', syncingId === source.id && 'animate-spin')} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => handleReconnect(source)}
                        disabled={connecting !== null}
                        title={source.providerEmail
                          ? `Reconnect ${source.providerEmail} (mailbox-mismatch-safe)`
                          : 'Reconnect this calendar'}
                      >
                        <RotateCw className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive"
                        onClick={() => handleDisconnect(source.id)}
                        disabled={disconnectingId === source.id}
                        title="Disconnect"
                      >
                        <Unlink className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
